import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpHeaders, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, defer, from, lastValueFrom, of, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthStateService } from '../services/auth/auth-state.service';
import { AuthService } from '../services/auth/auth.service';

/**
 * Interceptor de autenticación global.
 *
 * Responsabilidades:
 * - Adjuntar Authorization a peticiones que van al KONG_SERVICE_URL.
 * - Antes de enviar, si el token expira en ≤ 180s (o expiración inválida), refrescar.
 * - Si el backend responde 401, refrescar una vez y reintentar la petición.
 * - Serializa múltiples refrescos concurrentes para evitar carreras.
 */
let refreshInFlight: Promise<void> | null = null;

export function authInterceptor(req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
  const authState = inject(AuthStateService);
  const authService = inject(AuthService);

  // Sólo interceptar llamadas destinadas al gateway configurado en HttpGenericService
  const isTarget = typeof req.url === 'string' && req.url.startsWith(environment.KONG_SERVICE_URL);
  // Excluir endpoints públicos conocidos
  const isPublic =
    req.url.includes('login/auth') ||
    req.url.includes('token/refresh') ||
    req.url.includes('token/generar') ||
    req.url.includes('sw-emision/api/fianzas/cotizacion') ||
    req.url.includes('login/crearUsuario') ||
    // Validación de lavado de activos: se ejecuta durante el login, cuando aún no
    // existe sesión guardada. El token se adjunta explícitamente en el servicio,
    // por lo que el interceptor no debe intentar refrescarlo (provocaría logout).
    req.url.includes('sw-lav-cumplimiento/api/cumplimiento/validacion-cumplimiento') ||
    (req.url.includes('sw-core/api/pdf/generar') && !authState.tokenAcceso);
  if (!isTarget || isPublic) {
    return next(req);
  }

  return defer(async () => {
    // 1) Pre-refresh si está cerca de expirar
    const needPreRefresh = shouldRefreshNow(authState);
    if (needPreRefresh) {
      await refreshOnce(authService);
    }

    // 2) Adjuntar Authorization si hay token
    const token = authState.tokenAcceso;
    let headers = req.headers || new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    // No fijamos Content-Type aquí; lo maneja cada request según corresponda
    const authReq = req.clone({ headers });
    return authReq;
  }).pipe(
    switchMap((authReq) => next(authReq)),
    catchError((err) => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        // Intentar un refresh y reintentar UNA vez
        return from(refreshOnce(authService)).pipe(
          switchMap(() => {
            const token = authState.tokenAcceso;
            let headers = req.headers || new HttpHeaders();
            if (token) headers = headers.set('Authorization', `Bearer ${token}`);
            const retryReq = req.clone({ headers });
            return next(retryReq);
          }),
          catchError((refreshErr) => {
            // Si el refresh falla, cerrar sesión
            void authService.cerrarSesion();
            return throwError(() => refreshErr);
          }),
        );
      }
      return throwError(() => err);
    }),
  );
}

function shouldRefreshNow(authState: AuthStateService): boolean {
  try {
    const t = authState.token;
    const expRaw = t?.expiracion as string | number | undefined;
    let expMs = 0;
    if (typeof expRaw === 'number') {
      expMs = expRaw > 1e12 ? expRaw : expRaw * 1000;
    } else if (typeof expRaw === 'string') {
      const n = Number(expRaw);
      if (!Number.isNaN(n) && n > 0) expMs = n > 1e12 ? n : n * 1000;
      else expMs = new Date(expRaw).getTime();
    }
    const REFRESH_WINDOW_SECONDS = 180;
    if (!expMs) return true;
    const diffSec = Math.floor((expMs - Date.now()) / 1000);
    return diffSec <= REFRESH_WINDOW_SECONDS;
  } catch {
    return true;
  }
}

async function refreshOnce(authService: AuthService): Promise<void> {
  try {
    if (!refreshInFlight) {
      refreshInFlight = lastValueFrom(authService.refreshToken())
        .then(() => void 0)
        .finally(() => {
          refreshInFlight = null;
        });
    }
    await refreshInFlight;
  } catch (e) {
    // Propagar error para que el llamador decida cerrar sesión
    throw e;
  }
}
